import {
  Component,
  Input,
  OnInit,
  HostListener,
  Output,
  EventEmitter,
  TemplateRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import {
  PaTableColumn,
  PaTableData,
  PaTableAction,
  PaTableColumnType,
  PaTableGroupConfig,
  PaTableIconText
} from './models/pa-table.model';
import { PaFileListComponent } from '../pa-file-list/pa-file-list.component';
import { PaTagListComponent } from '../pa-tag-list/pa-tag-list.component';
import { IonInfiniteScroll, IonInfiniteScrollContent, IonIcon } from '@ionic/angular/standalone';
import { MetaPagination } from 'src/app/core/models/meta.interface';
import { FileFromServer } from 'src/app/core/models/files';
import { TooltipModule } from 'primeng/tooltip';
import { PaContextMenuComponent } from '../pa-context-menu/pa-context-menu.component';
import { recurrentPeriod } from 'src/app/features/tasks/tasks.const';
import { PaTableFilesCellComponent } from '../pa-table-files-cell/pa-table-files-cell.component';
import { PaTableArrayCellComponent } from '../pa-table-array-cell/pa-table-array-cell.component';
import { PaTableGroupRowComponent } from './components/pa-table-group-row/pa-table-group-row.component';
import { PaDataListComponent } from '../pa-data-list/pa-data-list.component';
import { PaTableRowToDataListPipe } from './pa-table-row-to-data-list.pipe';
import {
  PA_TABLE_DATE_FORMAT,
  PA_TABLE_DATE_LOCALE,
  PA_TABLE_DATETIME_FORMAT,
} from './pa-table-date-formats.constants';
import {
  buildGroups,
  getGroupKeyFromConfig,
  PaTableGroup,
} from './utils/pa-table-grouping.util';
import { PaBulkActionsBarComponent } from '../pa-bulk-actions-bar/pa-bulk-actions-bar.component';
import { PaBulkAction } from '../pa-bulk-actions-bar/models/pa-bulk-actions-bar.model';
import { addIcons } from 'ionicons';
import { repeat } from 'ionicons/icons';

addIcons({ repeat });

interface PaTableCardTemplateContext {
  $implicit: PaTableData;
  item: PaTableData;
  index: number;
  /**
   * Полный список действий таблицы (без фильтрации).
   */
  actions: PaTableAction[];
  /**
   * Запускает primary-действие строки (view/edit по текущим правилам таблицы).
   */
  triggerPrimaryAction: (item: PaTableData, event?: MouseEvent) => void;
}

@Component({
  selector: 'pa-table',
  templateUrl: './pa-table.component.html',
  styleUrls: ['./pa-table.component.scss'],
  standalone: true,
  imports: [
    IonIcon,
    IonInfiniteScrollContent,
    IonInfiniteScroll,
    CommonModule,
    FormsModule,
    TableModule,
    CheckboxModule,
    MenuModule,
    ButtonModule,
    PaginatorModule,
    PaFileListComponent,
    PaTagListComponent,
    PaContextMenuComponent,
    TooltipModule,
    PaTableFilesCellComponent,
    PaTableArrayCellComponent,
    PaTableGroupRowComponent,
    PaDataListComponent,
    PaTableRowToDataListPipe,
    PaBulkActionsBarComponent,
],
})
export class PaTableComponent implements OnInit, OnChanges {
  PaTableColumnType = PaTableColumnType;
  protected readonly tableDateFormat = PA_TABLE_DATE_FORMAT;
  protected readonly tableDateTimeFormat = PA_TABLE_DATETIME_FORMAT;
  protected readonly tableDateLocale = PA_TABLE_DATE_LOCALE;

  @Input() columns: PaTableColumn[] = [];
  @Input() data: PaTableData[] | null = null;
  /**
   * @deprecated полосатый вид будет удален
   */
  @Input() striped: boolean = false;
  @Input() selectable: boolean = false;
  @Input() actions: PaTableAction[] = [];
  @Input() actionsLabel: string = '';
  @Input() pagination: MetaPagination = null;
  @Input() groupBy: PaTableGroupConfig | null = null;
  
  /**
   * Функция для получения CSS класса для строки
   */
  @Input() rowClassFn?: (rowData: PaTableData) => string;


  /**
   * Функция для получения CSS класса для стрелки при наведении
   */
  @Input() hoverArrowClassFn?: (rowData: PaTableData) => string = () => 'pi-pencil';

  /**
   * Принудительно устанавливает режим отображения:
   * - true: только карточки (с бесконечной прокруткой)
   * - false: только таблица (с пагинацией)
   * - null: автоматический режим по ширине экрана
   */
  @Input() forceCardView: boolean | null = null;
  /**
   * Кастомный шаблон карточки для card-view.
   * Если не передан, используется дефолтный card-render.
   */
  @Input() cardItemTemplate?: TemplateRef<PaTableCardTemplateContext>;

  @Input() bulkActions: PaBulkAction[] = [];
  @Input() showBulkActionsBar: boolean = true;
  /**
   * Контекст аналитики для панели массовых действий.
   * Прокидывается в `pa-bulk-actions-bar`.
   */
  @Input() bulkActionsAnalyticsContext: string | null = null;

  @Output() selectionChange = new EventEmitter<PaTableData[]>();
  @Output() actionClick = new EventEmitter<{ action: PaTableAction; item: PaTableData }>();
  @Output() pageChange = new EventEmitter<PaginatorState>();
  @Output() infiniteScroll = new EventEmitter<{ target: { complete: () => void; disabled: boolean } }>();
  @Output() cameraTimeClick = new EventEmitter<{ pvzId: number; timestamp: number }>();
  /** События от панели массовых действий */
  @Output() bulkClose = new EventEmitter<void>();
  @Output() bulkToggleSelectAll = new EventEmitter<boolean>();
  @Output() bulkActionClick = new EventEmitter<PaBulkAction>();

  breakpoint: number = 1200;

  isCardView: boolean = false;
  selectedItems: PaTableData[] = [];
  isAllSelected: boolean = false;
  isIndeterminate: boolean = false;
  hoveredRowIndex: number | null = null;
  readonly triggerPrimaryActionFromTemplate = (item: PaTableData, event?: MouseEvent): void => this.onCellClick(item, event);

  get groups(): PaTableGroup[] {
    return buildGroups(this.data, this.groupBy);
  }
  private groupExpandedState = new Map<string, boolean>();

  ngOnInit(): void {
    this.checkViewMode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.hoveredRowIndex = null;
      const previousItems = changes['data'].previousValue as PaTableData[] | null | undefined;
      const currentItems = changes['data'].currentValue as PaTableData[] | null | undefined;
      const isAppendedData = this.isAppendedDataUpdate(previousItems, currentItems);

      if (isAppendedData) {
        // При infinite scroll сохраняем выбор уже загруженных строк.
        this.selectedItems = this.selectedItems.filter((item) => currentItems.includes(item));
        this.updateSelectAllState();
        return;
      }

      // При обычной смене данных (фильтры/страница) сбрасываем выбор.
      this.clearSelection();
    }
  }

  private isAppendedDataUpdate(previousItems?: PaTableData[] | null, currentItems?: PaTableData[] | null): boolean {
    if (!Array.isArray(previousItems) || !Array.isArray(currentItems)) {
      return false;
    }
    if (currentItems.length <= previousItems.length) {
      return false;
    }

    for (let i = 0; i < previousItems.length; i++) {
      if (currentItems[i] !== previousItems[i]) {
        return false;
      }
    }

    return true;
  }

  @HostListener('window:resize') onWindowResize(): void {
    this.checkViewMode();
  }

  private checkViewMode(): void {
    if (this.forceCardView !== null) {
      this.isCardView = this.forceCardView;
    } else {
      this.isCardView = window.innerWidth < this.breakpoint;
    }
  }

  /**
   * Получает количество колонок для группировки
   */
  get groupColspan(): number {
    let colspan = this.columns.length;
    if (this.selectable) {
      colspan += 1;
    }
    if (this.hasActions()) {
      colspan += 1;
    }
    return colspan;
  }

  onSelectAll(event: { checked?: boolean }): void {
    const items = this.data ?? [];
    if (event.checked) {
      this.selectedItems = [...items];
      this.isAllSelected = true;
    } else {
      this.selectedItems = [];
      this.isAllSelected = false;
    }
    this.isIndeterminate = false;
    this.selectionChange.emit(this.selectedItems);
  }

  onItemSelect(item: PaTableData, event?: { checked?: boolean }): void {
    if (event.checked) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = this.selectedItems.filter((selected) => selected !== item);
    }
    this.updateSelectAllState();
    this.selectionChange.emit(this.selectedItems);
  }

  isItemSelected(item: PaTableData): boolean {
    return this.selectedItems.includes(item);
  }

  private updateSelectAllState(): void {
    const selectedCount = this.selectedItems.length;
    const totalCount = this.data ? this.data.length : 0;
    this.isAllSelected = selectedCount === totalCount && totalCount > 0;
    this.isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
  }

  onActionClick(action: PaTableAction, item: PaTableData): void {
    if (action.command) {
      action.command(item);
    }
    this.actionClick.emit({ action, item });
  }

  hasActions(): boolean {
    return this.actions && this.actions.length > 0;
  }

  shouldShowCardHeader(item: PaTableData): boolean {
    return this.selectable || this.hasActions() || this.getCardTitle(item).length > 0;
  }

  getCardTitle(item: PaTableData): string {
    const primaryField = this.columns?.[0]?.field;
    if (primaryField) {
      const value = item[primaryField];
      if (value != null && String(value).trim().length > 0) {
        return String(value);
      }
    }

    const name = item['name'];
    if (typeof name === 'string' && name.trim().length > 0) {
      return name;
    }

    const title = item['title'];
    if (typeof title === 'string' && title.trim().length > 0) {
      return title;
    }

    return '';
  }

  getSelectedItems(): PaTableData[] {
    return [...this.selectedItems];
  }

  clearSelection(): void {
    this.selectedItems = [];
    this.isAllSelected = false;
    this.isIndeterminate = false;
    this.selectionChange.emit(this.selectedItems);
  }

  getSelectedCount(): number {
    return this.selectedItems.length;
  }

  get hasBulkActions(): boolean {
    return this.bulkActions && this.bulkActions.length > 0;
  }

  handleBulkCloseFromBar(): void {
    this.clearSelection();
    this.bulkClose.emit();
  }

  handleBulkToggleSelectAllFromBar(shouldSelect: boolean): void {
    this.onSelectAll({ checked: shouldSelect });
    this.bulkToggleSelectAll.emit(shouldSelect);
  }

  handleBulkActionClickFromBar(action: PaBulkAction): void {
    this.bulkActionClick.emit(action);
  }

  getFieldValue(item: PaTableData, field: string): unknown {    
    return item[field];
  }

  getFieldValueAsFiles(item: PaTableData, field: string): FileFromServer[] {
    const value = item[field];
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }

  getFieldValueAsTags(item: PaTableData, field: string): string[] {
    const value = item[field];
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }

  getFieldValueAsDate(item: PaTableData, field: string): Date | null {
    const value = item[field];
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  getFieldValueAsNumber(item: PaTableData, field: string): number {
    const value = item[field];
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      return parseFloat(value) || 0;
    }
    return 0;
  }

  getFieldValueAsBoolean(item: PaTableData, field: string): boolean {
    const value = item[field];
    return Boolean(value);
  }

  getFieldValueAsLink(item: PaTableData, field: string): { href: string; label: string } | null {
    const value = item[field];
    if (!value) {
      return null;
    }

    if (typeof value === 'string' && value.trim()) {
      return {
        href: value,
        label: value,
      };
    }

    if (typeof value !== 'object') {
      return null;
    }

    const linkValue = value as { href?: unknown; label?: unknown };
    if (typeof linkValue.href !== 'string' || !linkValue.href.trim()) {
      return null;
    }

    return {
      href: linkValue.href,
      label: typeof linkValue.label === 'string'
        ? linkValue.label
        : linkValue.href,
    };
  }

  /**
   * Форматирует время в секундах в формат H:MM
   */
  formatTime(seconds: number): string {
    if (seconds == null || isNaN(seconds)) {
      return '—';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * Форматирует время или возвращает плейсхолдер --:--
   */
  formatTimeOrPlaceholder(seconds: number | null | undefined): string {
    if (seconds == null || isNaN(seconds)) {
      return '--:--';
    }
    return this.formatTime(seconds);
  }

  /**
   * Обработчик клика по времени
   */
  onTimeClick(event: Event, pvzId: number, timestamp: number): void {
    event.stopPropagation();
    this.cameraTimeClick.emit({ pvzId, timestamp });
  }
  
  getFieldValueAsImageText(item: PaTableData, field: string): { name: string; image?: string } | null {
    const value = item[field];
    if (value && typeof value === 'object' && 'name' in value) {
      return value as { name: string; image?: string };
    }
    return null;
  }

  getFieldValueAsIconText(item: PaTableData, field: string): PaTableIconText | null {
    const value = item[field];
    if (typeof value === 'string') {
      return { text: value };
    }
    if (value === null || value === undefined || typeof value !== 'object') return null;

    const obj = value as Record<string, unknown>;
    if (!('text' in obj || 'icon' in obj)) return null;

    return obj as PaTableIconText;
  }

  /**
   * Проверяет, является ли значение date объектом с date
   */
  isDateObject(value: unknown): value is { date: string; recurrent_period: string } {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const obj = value as Record<string, unknown>;
    return (
      typeof obj.date === 'string'
    );
  }

  /**
   * Получает дату из due_date (может быть объектом или строкой)
   */
  getDueDate(item: PaTableData, field: string): Date | null {
    const value = item[field];
    if (!value) {
      return null;
    }

    if (this.isDateObject(value)) {
      return new Date(value.date);
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return new Date(value);
    }

    if (value instanceof Date) {
      return value;
    }

    return null;
  }

  /**
   * Получает текст периода из recurrent_period
   */
  getRecurrentPeriodText(periodId: string): string | null {
    const period = recurrentPeriod.find((p) => p.id === periodId);
    return period ? period.name : null;
  }

  getFieldValueAsFilesOrImages(item: PaTableData, field: string): Array<string | FileFromServer> {
    const value = item[field];
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }

  onPageChange(event: PaginatorState): void {
    this.pageChange.emit({
      first: event.first,
      rows: event.rows,
      page: event.page,
    });
  }

  onInfiniteScroll(event: { target: { complete: () => void; disabled: boolean } }): void {
    this.infiniteScroll.emit(event);
  }

  readonly getGroupKey = (item: PaTableData): string =>
    getGroupKeyFromConfig(item, this.groupBy);

  isNewGroup(rowData: PaTableData, rowIndex: number): boolean {
    if (!this.groupBy || !this.data) {
      return false;
    }
    if (rowIndex === 0) {
      return true;
    }
    const currentKey = this.getGroupKey(rowData);
    const prevRow = this.data[rowIndex - 1];
    if (!prevRow) {
      return true;
    }
    const prevKey = this.getGroupKey(prevRow);
    return currentKey !== prevKey;
  }

  isGroupExpanded(key: string): boolean {
    if (!this.groupBy) {
      return true;
    }
    if (!this.groupExpandedState.has(key)) {
      return true;
    }
    return Boolean(this.groupExpandedState.get(key));
  }

  handleGroupToggle(key: string): void {
    if (!this.groupBy) {
      return;
    }
    const current = this.isGroupExpanded(key);
    this.groupExpandedState.set(key, !current);
  }

  getGroupTitle(rowData: PaTableData): string {
    if (!this.groupBy) {
      return '';
    }
    const key = this.getGroupKey(rowData);
    const meta = this.groupBy.meta?.[key];
    if (meta?.name) {
      return meta.name;
    }
  }

  getGroupSubtitle(rowData: PaTableData): string | null {
    if (!this.groupBy) {
      return null;
    }
    const key = this.getGroupKey(rowData);
    const meta = this.groupBy.meta?.[key];
    if (meta?.description) {
      return meta.description;
    }
    return null;
  }

  onCellClick(item: PaTableData, event?: MouseEvent): void {
    if (this.shouldIgnoreCellClick(event)) {
      return;
    }
    const actionId = this.getPrimaryActionId(item);
    if (!actionId) {
      return;
    }
    const action = this.actions.find(
      (candidate) =>
        candidate.id === actionId
        && (!candidate.visible || candidate.visible(item)),
    );
    if (!action) {
      return;
    }
    this.onActionClick(action, item);
  }

  getCardTemplateContext(item: PaTableData, index: number): PaTableCardTemplateContext {
    return {
      $implicit: item,
      item,
      index,
      actions: this.actions,
      triggerPrimaryAction: this.triggerPrimaryActionFromTemplate,
    };
  }

  private getPrimaryActionId(item: PaTableData): string | null {
    const visibleActions = this.actions.filter(
      (action) => !action.visible || action.visible(item),
    );
    if (!visibleActions.length) {
      return null;
    }

    const hoverClass = this.hoverArrowClassFn?.(item);
    if (hoverClass === 'pi-check-square') {
      return (
        visibleActions.find((action) => action.id === 'confirm')?.id
        ?? visibleActions.find((action) => action.id === 'custom')?.id
        ?? visibleActions[0].id
      );
    }

    if (hoverClass === 'pi-eye') {
      return (
        visibleActions.find((action) => action.id === 'view')?.id
        ?? visibleActions[0].id
      );
    }

    const preferred = hoverClass === 'pi-pencil' ? 'edit' : 'view';
    return (
      visibleActions.find((action) => action.id === preferred)?.id
      ?? visibleActions[0].id
    );
  }

  private shouldIgnoreCellClick(event?: MouseEvent): boolean {
    if (!event) {
      return false;
    }

    const selection = window.getSelection();
    return Boolean(selection && !selection.isCollapsed && selection.toString().length > 0);
  }

  /**
   * Получает CSS класс для элемента на основе функции
   */
  getClass(item: PaTableData, fn: (item: PaTableData) => string): string {
    if (fn) {
      return fn(item);
    }
    return '';
  }

  trackByItemId(index: number, item: PaTableData): unknown {
    return item?.id ?? index;
  }
}
